"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.shouldNotify = exports.withNotificationPreferenceDefaults = exports.defaultNotificationPreferences = exports.DEFAULT_CHANNEL_PREFERENCE = exports.NotificationChannel = exports.ALERT_TYPE_CATEGORY = exports.ALERT_CATEGORIES = exports.AlertCategory = void 0;
const SensorAlert_1 = require("./SensorAlert");
/**
 * User-facing grouping of alert types for notification preferences. Toggling a
 * category affects every alert type mapped to it below — per-type toggles were
 * rejected as a UI (30+ switches per channel).
 */
var AlertCategory;
(function (AlertCategory) {
    AlertCategory["Water"] = "WATER";
    AlertCategory["Power"] = "POWER";
    AlertCategory["Hvac"] = "HVAC";
    AlertCategory["AirQuality"] = "AIR_QUALITY";
    /** Device health: sensors/hubs offline, dead radio, low battery. */
    AlertCategory["Connectivity"] = "CONNECTIVITY";
})(AlertCategory = exports.AlertCategory || (exports.AlertCategory = {}));
exports.ALERT_CATEGORIES = [
    AlertCategory.Water,
    AlertCategory.Power,
    AlertCategory.Hvac,
    AlertCategory.AirQuality,
    AlertCategory.Connectivity,
];
/**
 * Humidity lives under Water (humidity sensing ships on the leak detectors and
 * surfaces on the Water dashboard). Unknown/unmapped types deliberately have
 * no entry — notification dispatch treats them as always-on rather than
 * silently dropping an alert type someone forgot to map.
 */
exports.ALERT_TYPE_CATEGORY = {
    [SensorAlert_1.SensorAlertType.Leak]: AlertCategory.Water,
    [SensorAlert_1.SensorAlertType.LowHumidity]: AlertCategory.Water,
    [SensorAlert_1.SensorAlertType.HighHumidity]: AlertCategory.Water,
    [SensorAlert_1.SensorAlertType.LowEC]: AlertCategory.Water,
    [SensorAlert_1.SensorAlertType.HighEC]: AlertCategory.Water,
    [SensorAlert_1.SensorAlertType.WqSensorFault]: AlertCategory.Water,
    [SensorAlert_1.SensorAlertType.LowMCTCurrent]: AlertCategory.Power,
    [SensorAlert_1.SensorAlertType.HighMCTCurrent]: AlertCategory.Power,
    [SensorAlert_1.SensorAlertType.LowBCTCurrent]: AlertCategory.Power,
    [SensorAlert_1.SensorAlertType.HighBCTCurrent]: AlertCategory.Power,
    [SensorAlert_1.SensorAlertType.LowVoltage]: AlertCategory.Power,
    [SensorAlert_1.SensorAlertType.HighVoltage]: AlertCategory.Power,
    [SensorAlert_1.SensorAlertType.LowCurrent]: AlertCategory.Hvac,
    [SensorAlert_1.SensorAlertType.HighCurrent]: AlertCategory.Hvac,
    [SensorAlert_1.SensorAlertType.LowPressure]: AlertCategory.Hvac,
    [SensorAlert_1.SensorAlertType.HighPressure]: AlertCategory.Hvac,
    [SensorAlert_1.SensorAlertType.HVACfilter]: AlertCategory.Hvac,
    [SensorAlert_1.SensorAlertType.LowTC]: AlertCategory.Hvac,
    [SensorAlert_1.SensorAlertType.HighTC]: AlertCategory.Hvac,
    [SensorAlert_1.SensorAlertType.HvacDegraded]: AlertCategory.Hvac,
    [SensorAlert_1.SensorAlertType.HvacCtFault]: AlertCategory.Hvac,
    [SensorAlert_1.SensorAlertType.HvacCompressorFault]: AlertCategory.Hvac,
    [SensorAlert_1.SensorAlertType.HvacSensorFault]: AlertCategory.Hvac,
    [SensorAlert_1.SensorAlertType.HighIAQ]: AlertCategory.AirQuality,
    [SensorAlert_1.SensorAlertType.HighPM]: AlertCategory.AirQuality,
    [SensorAlert_1.SensorAlertType.HighVOC]: AlertCategory.AirQuality,
    [SensorAlert_1.SensorAlertType.HighCO2]: AlertCategory.AirQuality,
    [SensorAlert_1.SensorAlertType.LowBattery]: AlertCategory.Connectivity,
    [SensorAlert_1.SensorAlertType.SensorInactive]: AlertCategory.Connectivity,
    [SensorAlert_1.SensorAlertType.AqSensorInactive]: AlertCategory.Connectivity,
    [SensorAlert_1.SensorAlertType.HubInactive]: AlertCategory.Connectivity,
    [SensorAlert_1.SensorAlertType.HubOffline]: AlertCategory.Connectivity,
    [SensorAlert_1.SensorAlertType.HubRadioFault]: AlertCategory.Connectivity,
};
var NotificationChannel;
(function (NotificationChannel) {
    NotificationChannel["Sms"] = "sms";
    NotificationChannel["Email"] = "email";
    NotificationChannel["Push"] = "push";
})(NotificationChannel = exports.NotificationChannel || (exports.NotificationChannel = {}));
/**
 * Channels are OPT-IN: a user gets nothing until they enable a channel in the
 * app. The category and resolve fields default on so flipping a channel's
 * master switch means "every alert type, on create and resolve" — matching
 * the simplified one-toggle-per-channel UI.
 */
exports.DEFAULT_CHANNEL_PREFERENCE = {
    enabled: false,
    notifyOnResolve: true,
    water: true,
    power: true,
    hvac: true,
    airQuality: true,
    connectivity: true,
};
/** Opt-in default: all channels off. A user with no stored row gets exactly this. */
function defaultNotificationPreferences(userId) {
    return {
        userId,
        sms: { ...exports.DEFAULT_CHANNEL_PREFERENCE },
        email: { ...exports.DEFAULT_CHANNEL_PREFERENCE },
        push: { ...exports.DEFAULT_CHANNEL_PREFERENCE },
    };
}
exports.defaultNotificationPreferences = defaultNotificationPreferences;
const CATEGORY_FIELD = {
    [AlertCategory.Water]: 'water',
    [AlertCategory.Power]: 'power',
    [AlertCategory.Hvac]: 'hvac',
    [AlertCategory.AirQuality]: 'airQuality',
    [AlertCategory.Connectivity]: 'connectivity',
};
/**
 * Merge a possibly-partial stored record over the defaults. Fields added
 * after a user last saved (and rows written by older clients) resolve to
 * their default — categories/resolve to ON, channel master switches to OFF.
 */
function withNotificationPreferenceDefaults(userId, stored) {
    const base = defaultNotificationPreferences(userId);
    if (!stored)
        return base;
    return {
        ...base,
        sms: { ...base.sms, ...(stored.sms ?? {}) },
        email: { ...base.email, ...(stored.email ?? {}) },
        push: { ...base.push, ...(stored.push ?? {}) },
        updatedAt: stored.updatedAt ?? null,
    };
}
exports.withNotificationPreferenceDefaults = withNotificationPreferenceDefaults;
/**
 * The single decision point for "does this user want this notification on
 * this channel". `alertType` values outside the map (legacy/unknown) are
 * gated only by the channel master switch.
 */
function shouldNotify(prefs, channel, alertType, isResolveNotification) {
    const channelPrefs = prefs[channel];
    if (!channelPrefs?.enabled)
        return false;
    if (isResolveNotification && !channelPrefs.notifyOnResolve)
        return false;
    const category = exports.ALERT_TYPE_CATEGORY[alertType];
    if (!category)
        return true;
    return channelPrefs[CATEGORY_FIELD[category]] !== false;
}
exports.shouldNotify = shouldNotify;
